import { fetchToken } from "./requestConfig";

const MIN_RECONNECT_MS = 500;
const ERROR_RETRY_MS = 2000;
const MAX_RETRY_MS = 30000;

const wait = (ms, signal) =>
  new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      if (signal) {
        signal.removeEventListener("abort", finish);
      }
      window.removeEventListener("online", finish);
      document.removeEventListener("visibilitychange", onVisible);
      resolve();
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        finish();
      }
    };

    const timeoutId = window.setTimeout(finish, ms);
    if (signal) {
      signal.addEventListener("abort", finish, { once: true });
    }
    window.addEventListener("online", finish, { once: true });
    document.addEventListener("visibilitychange", onVisible);
  });

const parseJsonPayload = (text) => {
  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    return {};
  }
};

const withSubscriptionEvent = (data, eventName) => {
  const payload =
    data && typeof data === "object" && !Array.isArray(data) ? { ...data } : { content: data };
  if (eventName) {
    payload.event = eventName;
  }
  return payload;
};

const notifyFromSseChunk = (chunk, onMessage) => {
  let eventName = "";
  const dataLines = [];

  String(chunk || "")
    .split(/\r?\n/)
    .forEach((line) => {
      if (line.startsWith("event:")) {
        eventName = line.slice(6).trim();
        return;
      }
      if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trimStart());
      }
    });

  if (dataLines.length > 0 || eventName) {
    const data = dataLines.length > 0 ? parseJsonPayload(dataLines.join("\n")) : {};
    onMessage(withSubscriptionEvent(data, eventName));
    return;
  }

  const trimmed = chunk.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    onMessage(parseJsonPayload(trimmed));
  }
};

const consumeSseStream = async (response, onMessage, signal) => {
  if (!response.body || typeof response.body.getReader !== "function") {
    const text = await response.text();
    if (text) {
      onMessage(parseJsonPayload(text));
    }
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const cancelReader = () => {
    reader.cancel().catch(() => {});
  };
  signal.addEventListener("abort", cancelReader, { once: true });

  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        if (buffer) {
          notifyFromSseChunk(buffer, onMessage);
        }
        return;
      }

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop() || "";
      chunks.forEach((chunk) => {
        notifyFromSseChunk(chunk, onMessage);
      });
    }
  } finally {
    signal.removeEventListener("abort", cancelReader);
  }
};

const consumeLongPollResponse = async (response, onMessage) => {
  onMessage(parseJsonPayload(await response.text()));
};

/**
 * Authenticated GET that stays open until the server responds (long-poll) or
 * streams events (SSE). Any payload including `{}` is a change. Stream close
 * or network drop is a disconnect: reconnect after a short delay, never as a
 * tight loop, never as a permanent stop (unless abort() is called).
 */
export const startApiSubscription = ({
  apiPath,
  onMessage,
  retryDelayMs = ERROR_RETRY_MS,
  maxRetryDelayMs = MAX_RETRY_MS,
}) => {
  const javaApiUrl = process.env.REACT_APP_JAVA_API_URL;
  if (!javaApiUrl) {
    return () => {};
  }

  const controller = new AbortController();
  let retryDelay = retryDelayMs;

  const notify = (payload) => {
    if (typeof onMessage !== "function") {
      return;
    }
    try {
      onMessage(payload);
    } catch (error) {
      // Keep the subscription loop alive.
    }
  };

  const loop = async () => {
    while (!controller.signal.aborted) {
      try {
        const authToken = fetchToken();
        const requestHeaders = new Headers();
        requestHeaders.append("Accept", "application/json, text/event-stream");
        if (authToken !== "") {
          requestHeaders.append("Authorization", "Bearer " + authToken);
        }

        const response = await fetch(javaApiUrl + apiPath, {
          method: "GET",
          mode: "cors",
          headers: requestHeaders,
          signal: controller.signal,
        });

        if (controller.signal.aborted) {
          return;
        }

        if (response.status < 200 || response.status >= 300) {
          await wait(retryDelay, controller.signal);
          retryDelay = Math.min(retryDelay * 2, maxRetryDelayMs);
          continue;
        }

        retryDelay = retryDelayMs;
        const contentType = String(response.headers.get("content-type") || "").toLowerCase();
        if (contentType.includes("text/event-stream")) {
          await consumeSseStream(response, notify, controller.signal);
        } else {
          await consumeLongPollResponse(response, notify);
        }

        if (controller.signal.aborted) {
          return;
        }
        await wait(MIN_RECONNECT_MS, controller.signal);
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        await wait(retryDelay, controller.signal);
        retryDelay = Math.min(retryDelay * 2, maxRetryDelayMs);
      }
    }
  };

  loop().catch(() => {});

  return () => controller.abort();
};
